import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";
import { Typography, Box, CircularProgress } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PeopleIcon from "@mui/icons-material/People";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getInvoices } from "../../Actions/DashboardAction";
import { formatTwoDecimals } from "../../global/money";

export default function Dashboard() {
  const dispatch = useDispatch();
  const {
    isGettingInvoicesData,
    InvoicesDataList,
    chartYear,
    totalInvoices,
    validInvoices,
    invalidInvoices,
    registeredBuyers,
    unregisteredBuyers,
    totalBuyers,
  } = useSelector((state) => state.DashboardReducer);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getInvoices());
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  const validPercent = ((validInvoices || 0) / (totalInvoices || 0)) * 100;
  const registeredPercent = ((registeredBuyers || 0) / (totalBuyers || 0)) * 100;

  if (isGettingInvoicesData) {
    return (
      <Box
        sx={{
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: { xs: 2, md: 3 }, background: "#f8fafc", minHeight: "100vh" }}
    >
      {/* ===== PAGE HEADER ===== */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 800,
            color: "#0f172a",
            mb: 0.5,
          }}
        >
          Dashboard Overview
        </Typography>
      </Box>

      {/* ===== SUMMARY CARDS ===== */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Invoice Card */}
        <Box
          sx={{
            p: 3,
            borderRadius: "20px",
            background: "#fff",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            border: "1px solid #0000000f",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2
            }}
          >
            {/* LEFT SIDE */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #0f2044 0%, #1565C0 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <ReceiptLongIcon />
              </Box>

              <Typography sx={{ fontWeight: 700 }}>Invoice Summary</Typography>
            </Box>

            {/* RIGHT SIDE */}
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                Total Invoices
              </Typography>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 800,
                  color: "#0f172a",
                }}
              >
                {totalInvoices || 0}
              </Typography>
            </Box>
          </Box>

          {/* Progress */}
          <Box
            sx={{
              height: 8,
              borderRadius: 10,
              background: "#e2e8f0",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: `${validPercent}%`,
                height: "100%",
                background: "linear-gradient(90deg, #22c55e, #16a34a)",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ color: "#16a34a", fontWeight: 700 }}>
              ✔ {validInvoices || 0} Valid
            </Typography>
            <Typography sx={{ color: "#dc2626", fontWeight: 700 }}>
              ✖ {invalidInvoices || 0} Invalid
            </Typography>
          </Box>
        </Box>

        {/* Buyer Card */}
        <Box
          sx={{
            p: 3,
            borderRadius: "20px",
            background: "#fff",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            border: "1px solid #0000000f",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            {/* LEFT SIDE */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #312e81 0%, #6366f1 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <PeopleIcon />
              </Box>

              <Typography sx={{ fontWeight: 700 }}>Buyer Summary</Typography>
            </Box>

            {/* RIGHT SIDE */}
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                Total Buyers
              </Typography>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 800,
                  color: "#0f172a",
                }}
              >
                {totalBuyers || 0}
              </Typography>
            </Box>
          </Box>

          {/* Progress */}
          <Box
            sx={{
              height: 8,
              borderRadius: 10,
              background: "#e2e8f0",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: `${registeredPercent}%`,
                height: "100%",
                background: "linear-gradient(90deg, #6366f1, #4f46e5)",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ color: "#16a34a", fontWeight: 700 }}>
              ✔ {registeredBuyers || 0} Registered
            </Typography>
            <Typography sx={{ color: "#dc2626", fontWeight: 700 }}>
              ✖ {unregisteredBuyers || 0} Unregistered
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ===== CHART SECTION ===== */}
      <Box
        sx={{
          borderRadius: "24px",
          background: "#fff",
          boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
          border: "1px solid #0000000f",
          p: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 800,
              color: "#0f172a",
            }}
          >
            Monthly Invoice Analytics
          </Typography>
          {/* Bars go by each invoice's own date, never the day it was entered. */}
          <Typography sx={{ color: "#64748b", fontSize: "12px" }}>
            Valid invoices by invoice date
            {chartYear ? `, ${chartYear}` : ""}
          </Typography>
        </Box>

        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={InvoicesDataList || []}
            margin={{ top: 20, right: 20, bottom: 5, left: 0 }}
          >
            <defs>
              {["#6366f1", "#22c55e", "#f59e0b", "#f97316"].map((color, i) => (
                <linearGradient
                  key={i}
                  id={`barGrad${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={color} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.5} />
                </linearGradient>
              ))}
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e2e8f0"
            />

            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#64748b", fontSize: 12 }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#64748b", fontSize: 12 }}
            />

            <Tooltip
              formatter={(value) => formatTwoDecimals(value)}
              contentStyle={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            />

            <Bar dataKey="total" radius={[10, 10, 0, 0]}>
              {InvoicesDataList?.map((_, i) => (
                <Cell key={i} fill={`url(#barGrad${i % 4})`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
